import React from 'react';

import { silenceError } from '@nfctron/nexus-core';

import Cookies from 'js-cookie';
import isEqual from 'lodash/isEqual';

import {
	type AcceptCookiesOptions,
	COOKIE_CONSENT_KEY,
	type ConsentState,
	type CookieConsentHookParams,
	type CookieWrapper,
	DEFAULT_CONSENT,
	type DidDeclineAllHandler,
	type SetCookieProps,
} from './cookies.types';

export const allCookiesSetToValue = (value: boolean): Required<ConsentState> => ({
	session: value,
	persistent: value,
	/** necessary cookies are necessary, thus should stay active */
	necessary: true,
	preferences: value,
	statistics: value,
	marketing: value,
	firstParty: value,
	thirdParty: value,
});

export const allPropsApproved = (props: SetCookieProps, consent?: ConsentState): boolean => {
	if (!consent) return false;
	if (!consent[props.duration]) return false;
	if (!consent[props.provenance]) return false;
	if (!consent[props.purpose]) return false;
	return true;
};

/**
 * Hook to manage cookie consent
 * @see https://github.com/use-cookie-consent/use-cookie-consent
 * @param {CookieConsentHookParams | undefined} params
 */
export const useCookieConsent = (params?: CookieConsentHookParams) => {
	const storedConsent = silenceError(() => JSON.parse(Cookies.get(COOKIE_CONSENT_KEY)!));
	const initialConsent: ConsentState = storedConsent || params?.defaultConsent || DEFAULT_CONSENT;
	const [consent, setConsentState] = React.useState<ConsentState>(initialConsent);
	const setConsent = (newConsent: ConsentState) => {
		setConsentState(newConsent);
		Cookies.set(COOKIE_CONSENT_KEY, JSON.stringify(newConsent), {
			/** default cookie expiration to 1 year */
			expires: 365,
			...params?.consentCookieAttributes,
		});
	};

	const isConsentSet = React.useMemo(() => !isEqual(DEFAULT_CONSENT, consent), [consent]);

	const acceptCookies = (newConsent: ConsentState, options?: AcceptCookiesOptions) => {
		if (options?.allowChangingNecessary) {
			setConsent(newConsent);
		} else {
			setConsent({ ...newConsent, necessary: true });
		}
	};

	const declineAllCookies = () => setConsent(allCookiesSetToValue(false));
	const acceptAllCookies = () => setConsent(allCookiesSetToValue(true));

	const didAcceptAll = (): boolean => {
		const keyArray = Object.keys(consent || {}) as (keyof ConsentState)[];
		return keyArray.reduce<boolean>((prev, key) => (prev && consent && consent[key]) || false, true);
	};

	const didDeclineAll: DidDeclineAllHandler = (opts) => {
		const keyArray = Object.keys(consent || {}) as (keyof ConsentState)[];

		return keyArray.reduce<boolean>((prev, key): boolean => {
			if (!opts?.includingNecessary && key === 'necessary') return prev;
			return (prev && consent && !consent[key]) || false;
		}, true);
	};

	const cookieWrapper: CookieWrapper = {
		set: (name, value, props, options) => {
			if (!allPropsApproved(props, consent)) return undefined;
			return Cookies.set(name, JSON.stringify(value), options);
		},
		get: Cookies.get,
		getAll: Cookies.get,
		remove: Cookies.remove,
	};

	return {
		consent,
		acceptCookies,
		declineAllCookies,
		acceptAllCookies,
		didAcceptAll,
		didDeclineAll,
		isConsentSet,
		cookies: cookieWrapper,
	};
};
